// Real-User Monitoring (RUM) for Core Web Vitals. Wired in `index.js`.
//
// Why our own RUM instead of relying on Chrome CrUX:
// 1. CrUX only publishes field data when a site has ~500+ unique
//    Chrome-sync users per 28-day window. HavenCostGuide is deep in
//    HCU recovery — traffic isn't there yet.
// 2. Even after traffic returns, CrUX aggregates at origin-level, not
//    per-page — we can't tell which pages have slow LCP.
// 3. This pipeline stores per-page + per-device samples in Mongo so we
//    can compute p50/p75/p95 at any granularity in the admin dashboard.
//
// Send strategy: sendBeacon for reliability at page-unload; falls back
// to fetch with keepalive.

import { onCLS, onFCP, onINP, onLCP, onTTFB } from "web-vitals";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function detectDevice() {
  if (typeof navigator === "undefined") return "unknown";
  // Coarse but works — matches how CrUX buckets. Do not use UA parsing.
  return matchMedia("(max-width: 767px)").matches ? "mobile" : "desktop";
}

function detectConnection() {
  const c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  return c?.effectiveType || "unknown";
}

function send(payload) {
  // /api/vitals is public-write (no auth) but rate-limited backend-side
  // AND validated (metric name, url pattern, value bounds). Doing this
  // via sendBeacon keeps it non-blocking + reliable at unload.
  const url = `${API}/vitals`;
  try {
    const body = JSON.stringify(payload);
    if (navigator.sendBeacon) {
      const ok = navigator.sendBeacon(url, new Blob([body], { type: "application/json" }));
      if (ok) return;
    }
    // Fallback — keepalive lets the request outlive the page unload
    fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
    }).catch(() => {});
  } catch {
    // Silent — never break the page for analytics
  }
}

function report(metric) {
  // Skip synthetic + very short sessions where the value isn't meaningful
  if (!metric || metric.value == null) return;
  send({
    metric: metric.name,               // "LCP" | "INP" | "CLS" | "FCP" | "TTFB"
    value: Number(metric.value),
    id: metric.id,                     // deduplication key for late updates
    delta: Number(metric.delta || 0),
    navigation_type: metric.navigationType || "unknown",
    rating: metric.rating || "unknown", // "good" | "needs-improvement" | "poor"
    url: window.location.pathname,     // path only — no query, no hash
    device: detectDevice(),
    connection: detectConnection(),
  });
}

export function initVitalsRUM() {
  try {
    onLCP(report, { reportAllChanges: false });
    onINP(report);
    onCLS(report);
    onFCP(report);
    onTTFB(report);
  } catch {
    // web-vitals package not loaded / not supported in this browser
  }
}
